import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { LoginPage } from '../auth/LoginPage';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { SsoCallbackPage } from '../auth/SsoCallbackPage';
import { RequirePlatformAdmin } from '../auth/RequireRole';
import { AppLayout } from './AppLayout';
import { MobileLayout } from '../mobile/MobileLayout';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}
import { DashboardPage } from './DashboardPage';
import { DashboardEmailTab } from './DashboardEmailTab';
import { DashboardCalendarTab } from './DashboardCalendarTab';
import { DashboardTasksTab } from './DashboardTasksTab';
import { DashboardContactsTab } from './DashboardContactsTab';
import { AdminLayout } from '../admin/AdminLayout';
import { AdminUsersPage } from '../admin/AdminUsersPage';
import { AdminTenantsPage } from '../admin/AdminTenantsPage';
import { AdminSsoPage } from '../admin/AdminSsoPage';
import { AdminExternalLinksPage } from '../admin/AdminExternalLinksPage';
import { AdminCustomizePage } from '../admin/AdminCustomizePage';
import { AdminEventsPage } from '../admin/AdminEventsPage';
import { AdminSslPage } from '../admin/AdminSslPage';
import { AdminCompanyPage } from '../admin/AdminCompanyPage';
import { AdminProfileDetailPage } from '../admin/AdminProfileDetailPage';
import { AdminAiSettingsPage } from '../admin/AdminAiSettingsPage';
import { AdminModulesPage } from '../admin/AdminModulesPage';
import { ProfilePage } from '../profile/ProfilePage';
import { FeatureRequestsPage } from '../feature-requests/FeatureRequestsPage';
import { ExpertProfilesPage } from '../expert-profiles/ExpertProfilesPage';
import { ExpertProfileDetailPage } from '../expert-profiles/ExpertProfileDetailPage';
import { CrmModulePage } from '../crm/CrmModulePage';
import { CompanyDetailPage } from '../crm/companies/CompanyDetailPage';
import { ContractDetailPage } from '../crm/companies/ContractDetailPage';
import { ContactDetailPage } from '../crm/contacts/ContactDetailPage';
import { CrmSettingsProvider } from '../crm/settings/CrmSettingsContext';
import { InfoHubPage } from '../infohub/InfoHubPage';
import { CtbModulePage } from '../ctb/CtbModulePage';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleUnauthorized = () => navigate('/login', { replace: true });
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Laden...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function App() {
  const isMobile = useIsMobile();

  return (
    <Routes>
      {/* Öffentliche Routen */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />

      {/* Geschützte Routen */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <ErrorBoundary>
              {isMobile ? <MobileLayout /> : <AppLayout />}
            </ErrorBoundary>
          </PrivateRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="emails" element={<DashboardEmailTab />} />
        <Route path="calendar" element={<DashboardCalendarTab />} />
        <Route path="tasks" element={<DashboardTasksTab />} />
        <Route path="outlook-contacts" element={<DashboardContactsTab />} />
        <Route path="feature-requests" element={<FeatureRequestsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        {/* Experten Profil (expert-profile-service auf APS01:3100) */}
        <Route path="expert-profile" element={<ExpertProfilesPage />} />
        <Route path="expert-profiles/:userId" element={<ExpertProfileDetailPage />} />
        {/* CRM-Modul (alle Routes im CrmSettingsProvider) */}
        <Route path="crm" element={<CrmSettingsProvider><CrmModulePage /></CrmSettingsProvider>} />
        <Route path="crm/companies/:id" element={<CrmSettingsProvider><CompanyDetailPage /></CrmSettingsProvider>} />
        <Route path="crm/companies/:companyId/contracts/:contractId" element={<CrmSettingsProvider><ContractDetailPage /></CrmSettingsProvider>} />
        <Route path="crm/contacts/:id" element={<CrmSettingsProvider><ContactDetailPage /></CrmSettingsProvider>} />
        {/* Info Hub (nativ integriert) */}
        <Route path="infohub/*" element={<InfoHubPage />} />
        {/* Compliance Tool Box */}
        <Route path="ctb/*" element={<CtbModulePage />} />
        {/* Admin-Bereich mit eigenem Layout (nur PLATFORM_ADMIN) */}
        <Route path="admin" element={<RequirePlatformAdmin><AdminLayout /></RequirePlatformAdmin>}>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="modules" element={<AdminModulesPage />} />
          <Route path="tenants" element={<AdminTenantsPage />} />
          <Route path="sso" element={<AdminSsoPage />} />
          <Route path="external-links" element={<AdminExternalLinksPage />} />
          <Route path="customize" element={<AdminCustomizePage />} />
          <Route path="company" element={<AdminCompanyPage />} />
          <Route path="events" element={<AdminEventsPage />} />
          <Route path="ssl" element={<AdminSslPage />} />
          <Route path="ai-settings" element={<AdminAiSettingsPage />} />
          <Route path="feature-requests" element={<FeatureRequestsPage />} />
        </Route>
        {/* Admin-Profildetail außerhalb des Admin-Layouts (volle Seite) */}
        <Route path="admin/profiles/:userId" element={<RequirePlatformAdmin><AdminProfileDetailPage /></RequirePlatformAdmin>} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
